/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { audioContext } from "./utils";
import AudioRecordingWorklet from "./worklets/audio-processing";
import VolMeterWorket from "./worklets/vol-meter";

import { createWorketFromSrc } from "./audioworklet-registry";
import EventEmitter from "eventemitter3";

function arrayBufferToBase64(buffer: ArrayBuffer) {
  var binary = "";
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: MediaStreamAudioSourceNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  vuWorklet: AudioWorkletNode | undefined;
  micGainNode: GainNode | undefined; // For gain-based muting (keeps stream active)

  private starting: Promise<void> | null = null;

  constructor(public sampleRate = 16000) {
    super();
  }

  async start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Could not request user media");
    }

    this.starting = new Promise(async (resolve, reject) => {
      try {
        // Mobile-specific audio constraints
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        // iOS Safari has very strict audio constraints - use minimal constraints
        const audioConstraints = {
          audio: isIOS ? {
            // Minimal constraints for iOS Safari compatibility
            echoCancellation: true,
            // Don't use noiseSuppression and autoGainControl on iOS - not supported
            sampleRate: 16000,
            channelCount: 1
          } : {
            // Full constraints for other browsers
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            ...(isMobile && {
              sampleRate: 16000,
              channelCount: 1,
              volume: 1.0,
              // Try to get better audio quality on mobile
              googEchoCancellation: true,
              googNoiseSuppression: true,
              googAutoGainControl: true,
              googHighpassFilter: true,
              googTypingNoiseDetection: true,
            })
          }
        };

        console.log(`[AudioRecorder] Starting audio capture on ${isIOS ? 'iOS' : isMobile ? 'mobile' : 'desktop'} device`);
        console.log('[AudioRecorder] Using audio constraints:', audioConstraints);
        
        this.stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
        
        // Check if stream is active
        if (!this.stream || this.stream.getTracks().length === 0) {
          throw new Error("No audio tracks available");
        }

        const audioTracks = this.stream.getAudioTracks();
        console.log(`[AudioRecorder] Got ${audioTracks.length} audio track(s):`, audioTracks.map(track => ({
          label: track.label,
          enabled: track.enabled,
          readyState: track.readyState,
          settings: track.getSettings()
        })));

        // Create audio context with mobile-friendly settings
        const contextOptions = isMobile ? {
          sampleRate: this.sampleRate,
          latencyHint: 'interactive' as AudioContextLatencyCategory
        } : { sampleRate: this.sampleRate };
        
        // For iOS, create AudioContext directly to avoid the audio play attempt in utils
        if (isIOS) {
          console.log('[AudioRecorder] Creating direct AudioContext for iOS');
          this.audioContext = new AudioContext(contextOptions);
        } else {
          this.audioContext = await audioContext(contextOptions);
        }
        
        // Critical: Resume audio context for mobile browsers (required after user gesture)
        if (this.audioContext.state === 'suspended') {
          console.log('[AudioRecorder] Audio context is suspended, attempting to resume...');
          try {
            await this.audioContext.resume();
            console.log('[AudioRecorder] Audio context resumed successfully');
          } catch (resumeError) {
            console.error('[AudioRecorder] Failed to resume audio context:', resumeError);
            throw new Error('Failed to resume audio context. Please try clicking the microphone button again.');
          }
        }
        
        console.log('[AudioRecorder] Audio context state:', this.audioContext.state);
        
        this.source = this.audioContext.createMediaStreamSource(this.stream);

        // Create gain node for muting control (keeps stream active)
        this.micGainNode = this.audioContext.createGain();
        this.micGainNode.gain.value = 1.0; // Start unmuted

        const workletName = "audio-recorder-worklet";
        const src = createWorketFromSrc(workletName, AudioRecordingWorklet);

        await this.audioContext.audioWorklet.addModule(src);
        this.recordingWorklet = new AudioWorkletNode(
          this.audioContext,
          workletName,
        );

        this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
          // worklet processes recording floats and messages converted buffer
          const arrayBuffer = ev.data.data.int16arrayBuffer;

          if (arrayBuffer) {
            const arrayBufferString = arrayBufferToBase64(arrayBuffer);
            this.emit("data", arrayBufferString);
          }
        };
        
        // Connect through gain node: source -> gain -> worklet
        // This allows muting without stopping the stream
        this.source.connect(this.micGainNode);
        this.micGainNode.connect(this.recordingWorklet);

        // vu meter worklet
        const vuWorkletName = "vu-meter";
        await this.audioContext.audioWorklet.addModule(
          createWorketFromSrc(vuWorkletName, VolMeterWorket),
        );
        this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
        this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
          this.emit("volume", ev.data.volume);
        };

        this.source.connect(this.vuWorklet);
        this.recording = true;
        resolve();
        this.starting = null;
        
      } catch (error) {
        console.error('[AudioRecorder] Error starting audio recording:', error);
        
        // Provide iOS-specific error messages
        if (error instanceof DOMException) {
          switch (error.name) {
            case 'NotAllowedError':
              alert('🎤 Microphone access denied.\n\nOn iPhone:\n1. Check Safari Settings > Privacy & Security > Camera & Microphone\n2. Make sure this site is allowed\n3. Try refreshing the page');
              break;
            case 'NotFoundError':
              alert('🎤 No microphone found. Please check your device settings.');
              break;
            case 'NotSupportedError':
              alert('🎤 Microphone not supported in this browser. Try using Safari on iPhone.');
              break;
            case 'OverconstrainedError':
              console.log('[AudioRecorder] Audio constraints too strict, trying fallback...');
              // Try with minimal constraints as fallback
              try {
                const fallbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.stream = fallbackStream;
                console.log('[AudioRecorder] Fallback audio stream created');
                // Continue with minimal setup for fallback
                this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
                if (this.audioContext.state === 'suspended') {
                  await this.audioContext.resume();
                }
                this.source = this.audioContext.createMediaStreamSource(this.stream);
                
                // Create gain node for fallback path
                this.micGainNode = this.audioContext.createGain();
                this.micGainNode.gain.value = 1.0;
                
                const workletName = "audio-recorder-worklet";
                const src = createWorketFromSrc(workletName, AudioRecordingWorklet);
                await this.audioContext.audioWorklet.addModule(src);
                this.recordingWorklet = new AudioWorkletNode(this.audioContext, workletName);
                this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
                  const arrayBuffer = ev.data.data.int16arrayBuffer;
                  if (arrayBuffer) {
                    const arrayBufferString = arrayBufferToBase64(arrayBuffer);
                    this.emit("data", arrayBufferString);
                  }
                };
                this.source.connect(this.micGainNode);
                this.micGainNode.connect(this.recordingWorklet);
                
                const vuWorkletName = "vu-meter";
                await this.audioContext.audioWorklet.addModule(createWorketFromSrc(vuWorkletName, VolMeterWorket));
                this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
                this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
                  this.emit("volume", ev.data.volume);
                };
                
                // Also connect vuWorklet through gain node for consistency
                this.source.connect(this.vuWorklet);
                
                this.recording = true;
                resolve();
                this.starting = null;
                return;
              } catch (fallbackError) {
                console.error('[AudioRecorder] Fallback also failed:', fallbackError);
                alert('🎤 Microphone setup failed. Please try again.');
              }
              break;
            default:
              alert(`🎤 Microphone error: ${error.message}`);
          }
        }
        
        // Clean up on error
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
          this.stream = undefined;
        }
        if (this.audioContext) {
          this.audioContext.close();
          this.audioContext = undefined;
        }
        reject(error);
        this.starting = null;
      }
    });
  }

  stop() {
    // its plausible that stop would be called before start completes
    // such as if the websocket immediately hangs up
    const handleStop = () => {
      this.source?.disconnect();
      this.stream?.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
      this.recordingWorklet = undefined;
      this.vuWorklet = undefined;
    };
    if (this.starting) {
      this.starting.then(handleStop);
      return;
    }
    handleStop();
  }

  /**
   * Mute microphone using gain control (keeps stream active - critical for mobile)
   */
  muteMic() {
    if (this.micGainNode && this.audioContext) {
      console.log('[AudioRecorder] Muting microphone via gain node');
      this.micGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    }
  }

  /**
   * Unmute microphone using gain control
   */
  unmuteMic() {
    if (this.micGainNode && this.audioContext) {
      console.log('[AudioRecorder] Unmuting microphone via gain node');
      this.micGainNode.gain.setValueAtTime(1.0, this.audioContext.currentTime);
    }
  }
}